import * as React from "react";
import { Check, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAccountLabelHelpers } from "./useAccountLabelHelpers";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AccountItemContentProps {
  accountName: string;
  typeLabel: string;
}

export const AccountItemContent = React.memo(({
  accountName,
  typeLabel,
}: AccountItemContentProps) => {
  const containerRef = React.useRef<HTMLSpanElement>(null);
  const accountNameRef = React.useRef<HTMLSpanElement>(null);
  const typeLabelRef = React.useRef<HTMLSpanElement>(null);
  const [isNameTruncated, setIsNameTruncated] = React.useState(false);
  const [isTypeTruncated, setIsTypeTruncated] = React.useState(false);

  React.useEffect(() => {
    const checkTruncation = () => {
      if (accountNameRef.current) {
        setIsNameTruncated(accountNameRef.current.scrollWidth > accountNameRef.current.clientWidth);
      }
      if (typeLabelRef.current) {
        setIsTypeTruncated(typeLabelRef.current.scrollWidth > typeLabelRef.current.clientWidth);
      }
    };

    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [accountName, typeLabel]);

  const showTooltip = isNameTruncated || isTypeTruncated;

  return (
    <span ref={containerRef} className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
      <span
        ref={accountNameRef}
        className="truncate max-w-[300px]"
      >
        {accountName}
      </span>
      {typeLabel && (
        <span
          ref={typeLabelRef}
          className="italic text-muted-foreground text-xs truncate min-w-0 flex-shrink"
        >
          {typeLabel}
        </span>
      )}
      {showTooltip && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Info className="h-3 w-3 flex-shrink-0 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <p className="font-medium">{accountName}</p>
              {typeLabel && <p className="text-xs text-muted-foreground italic">{typeLabel}</p>}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
  );
});

AccountItemContent.displayName = 'AccountItemContent';

export interface AccountOption {
  id: string;
  account_name: string;
  account_number?: string;
  type?: {
    type_name: string;
  };
  category?: {
    category_name: string;
  };
  /**
   * Canonical sign direction for this account. Read directly from
   * chart_of_accounts.normal_balance — bookkeepers can intentionally set a
   * contra account opposite its category (e.g. an Expense-category account
   * with normal_balance='credit' for refunds/discounts). Used by
   * getAccountSignForForm to compute line-item sign on AP/AR/expense forms.
   */
  normal_balance?: 'debit' | 'credit' | null;
  parent_account_id?: string | null;
  default_tax_rate_id?: string | null;
  default_tax_rate?: { id: string; name: string; rate: number; direction: string } | null;
  currency?: string | null;
  system_account_type?: string | null;
}

interface AccountSelectPanelProps {
  accounts: AccountOption[];
  value?: string;
  onSelect: (value: string, account?: AccountOption) => void;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Category name(s) to show first in the list. Can be a single category or array for multi-level priority. */
  prioritizeCategory?: string | string[];
  /** Account type name(s) to show first, before category grouping. E.g., ["Bank Account", "Credit Card"] */
  prioritizeType?: string | string[];
  /** Account IDs to pin at the top of the dropdown in a separate group */
  pinnedAccountIds?: string[];
  /** Label for the pinned accounts group */
  pinnedLabel?: string;
  /** Render a "Clear" item at the top that calls onSelect('') */
  clearable?: boolean;
  clearLabel?: string;
  /** Force currencies onto every type label — see `useAccountLabelHelpers`. */
  showCurrency?: boolean;
}

export function AccountSelectPanel({
  accounts,
  value,
  onSelect,
  searchPlaceholder = "Search accounts...",
  emptyText = "No accounts found.",
  prioritizeCategory,
  prioritizeType,
  pinnedAccountIds,
  pinnedLabel = "Suggested",
  clearable = false,
  clearLabel = "Clear",
  showCurrency,
}: AccountSelectPanelProps) {
  // Display name + type/currency label come from the shared hook so the rows
  // here and a parent's trigger can never disagree.
  const { getDisplayName, buildTypeLabel } = useAccountLabelHelpers(accounts, { showCurrency });

  const pinnedAccountIdSet = React.useMemo(
    () => new Set(pinnedAccountIds ?? []),
    [pinnedAccountIds]
  );

  const pinnedAccounts = React.useMemo(
    () => accounts.filter((a) => pinnedAccountIdSet.has(a.id)),
    [accounts, pinnedAccountIdSet]
  );

  const sortedAccounts = React.useMemo(() => {
    const defaultCategoryOrder = ['Expenses', 'Assets', 'Liabilities', 'Equity', 'Revenue'];

    const priorityTypes = prioritizeType
      ? Array.isArray(prioritizeType)
        ? prioritizeType
        : [prioritizeType]
      : [];

    const priorityCategories = prioritizeCategory
      ? Array.isArray(prioritizeCategory)
        ? prioritizeCategory
        : [prioritizeCategory]
      : [];

    let categoryOrder: string[];
    if (priorityCategories.length > 0) {
      const remaining = defaultCategoryOrder.filter((c) => !priorityCategories.includes(c));
      categoryOrder = [...priorityCategories, ...remaining];
    } else {
      categoryOrder = defaultCategoryOrder;
    }

    const buildHierarchicalList = (accountList: AccountOption[]): AccountOption[] => {
      const parents = accountList.filter((a) => !a.parent_account_id);
      const childrenMap = new Map<string, AccountOption[]>();

      accountList.forEach((account) => {
        if (account.parent_account_id) {
          if (!childrenMap.has(account.parent_account_id)) {
            childrenMap.set(account.parent_account_id, []);
          }
          childrenMap.get(account.parent_account_id)!.push(account);
        }
      });

      parents.sort((a, b) => a.account_name.localeCompare(b.account_name));

      childrenMap.forEach((children) => {
        children.sort((a, b) => a.account_name.localeCompare(b.account_name));
      });

      const result: AccountOption[] = [];
      parents.forEach((parent) => {
        result.push(parent);
        const children = childrenMap.get(parent.id);
        if (children) {
          result.push(...children);
        }
      });

      return result;
    };

    const result: AccountOption[] = [];
    const usedAccountIds = new Set<string>(pinnedAccountIdSet);

    if (priorityTypes.length > 0) {
      priorityTypes.forEach((typeName) => {
        const typeAccounts = accounts.filter(
          (acc) => acc.type?.type_name === typeName && !usedAccountIds.has(acc.id)
        );
        const hierarchical = buildHierarchicalList(typeAccounts);
        hierarchical.forEach((acc) => usedAccountIds.add(acc.id));
        result.push(...hierarchical);
      });
    }

    const remainingAccounts = accounts.filter((acc) => !usedAccountIds.has(acc.id));
    const grouped = new Map<string, AccountOption[]>();

    remainingAccounts.forEach((account) => {
      const categoryName = account.category?.category_name || 'Other';
      if (!grouped.has(categoryName)) {
        grouped.set(categoryName, []);
      }
      grouped.get(categoryName)!.push(account);
    });

    categoryOrder.forEach((category) => {
      const accts = grouped.get(category);
      if (accts) {
        result.push(...buildHierarchicalList(accts));
        grouped.delete(category);
      }
    });

    grouped.forEach((accts) => {
      result.push(...buildHierarchicalList(accts));
    });

    return result;
  }, [accounts, prioritizeCategory, prioritizeType, pinnedAccountIdSet]);

  const getCommandValue = React.useCallback((account: AccountOption) => {
    const typeLabel = buildTypeLabel(account);
    return `${getDisplayName(account)} ${typeLabel}`;
  }, [buildTypeLabel, getDisplayName]);

  const selectedAccount = accounts.find((account) => account.id === value);

  // Pre-highlight the currently selected account when the panel mounts (e.g.,
  // when its parent Popover opens). The Command component highlights its
  // matching CommandItem when value === item value.
  const [highlightedValue, setHighlightedValue] = React.useState<string>(
    selectedAccount ? getCommandValue(selectedAccount) : ""
  );

  // If the panel mounted before `accounts` had resolved (e.g., the user
  // clicked the trigger while useChartOfAccounts was still in flight), the
  // initial state above will be "" because selectedAccount was undefined.
  // Sync the highlight once when the selected account first becomes
  // resolvable. Done with a ref so we never override the user's own search
  // typing after that first sync.
  const didInitializeHighlight = React.useRef(highlightedValue !== "");
  React.useEffect(() => {
    if (!didInitializeHighlight.current && selectedAccount) {
      setHighlightedValue(getCommandValue(selectedAccount));
      didInitializeHighlight.current = true;
    }
  }, [selectedAccount, getCommandValue]);

  return (
    <Command value={highlightedValue} onValueChange={setHighlightedValue}>
      <CommandInput placeholder={searchPlaceholder} />
      <CommandList>
        <CommandEmpty>{emptyText}</CommandEmpty>
        {clearable && (
          <CommandGroup>
            <CommandItem
              value=" "
              onSelect={() => onSelect('')}
              className="text-muted-foreground italic"
            >
              {clearLabel}
            </CommandItem>
          </CommandGroup>
        )}
        {pinnedAccounts.length > 0 && (
          <>
            <CommandGroup heading={pinnedLabel}>
              {pinnedAccounts.map((account) => {
                const typeLabel = buildTypeLabel(account);
                const isSelected = value === account.id;
                return (
                  <CommandItem
                    key={`pinned-${account.id}`}
                    value={`${getDisplayName(account)} ${typeLabel}`}
                    onSelect={() => onSelect(account.id === value ? "" : account.id, account)}
                    className={cn(isSelected && "bg-accent")}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 flex-shrink-0",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <AccountItemContent
                      accountName={getDisplayName(account)}
                      typeLabel={typeLabel}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup>
          {sortedAccounts.map((account) => {
            const isSubAccount = !!account.parent_account_id;
            const typeLabel = buildTypeLabel(account);
            const isSelected = value === account.id;

            return (
              <CommandItem
                key={account.id}
                value={`${getDisplayName(account)} ${typeLabel}`}
                onSelect={() => onSelect(account.id === value ? "" : account.id, account)}
                className={cn(
                  isSubAccount && "pl-10",
                  isSelected && "bg-accent"
                )}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4 flex-shrink-0",
                    value === account.id ? "opacity-100" : "opacity-0"
                  )}
                />
                <AccountItemContent
                  accountName={getDisplayName(account)}
                  typeLabel={typeLabel}
                />
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

